import Cta from "@components/Cta/Cta";
import Grid from "@components/Grid/Grid";
import { NextContentfulImage } from "@components/Image";
import Link from "@components/Link/Link";
import { Image } from "@customTypes/image";
import { Link as LinkType } from "@customTypes/link";
import { richTextifyEnhanced } from "@utilities/richtext";
import clsx from "clsx";
import { useInView } from "react-hook-inview";
import styles from "./C20_PromotionalArticles.module.scss";

export interface PromotionalArticlesProps {
  imageFullWide: Image;
  imageWide: Image;
  imageMedium: Image;
  imageSmall: Image;
  imageMobile: Image;
  label: string;
  title: string;
  titleEnhanced?: Document;
  titleEnhancedStyle?: string;
  description: string;
  articleLink: LinkType;
  linkUrlForRender: LinkType;
  reverse: boolean;
  isLight: boolean;
  isSeventyPercentVariation: boolean;
}

export const PromotionalArticles = (props: {
  content: PromotionalArticlesProps;
}) => {
  const { content } = props;
  const {
    imageFullWide,
    imageWide,
    imageMedium,
    imageSmall,
    imageMobile,
    label,
    title,
    titleEnhanced,
    titleEnhancedStyle,
    description,
    articleLink,
    linkUrlForRender,
    reverse,
    isLight,
    isSeventyPercentVariation,
  } = content;

  const titleToRender = titleEnhanced
    ? richTextifyEnhanced(titleEnhanced)
    : title;

  const titleUrl = linkUrlForRender?.fields?.url;

  const getHighlightStyle = (style: string) => {
    if (!isLight) {
      return style === "amber" || style === "sky" ? style : "";
    }
    return style || "";
  };

  const titleStyle = getHighlightStyle(titleEnhancedStyle);

  const [ref, isVisible] = useInView({
    threshold: 0,
    unobserveOnEnter: true,
  });

  let promotionalType = 0;
  let cssClass = "";

  if (imageMedium) {
    promotionalType = 1;
  } else if (imageFullWide && imageWide) {
    promotionalType = 2;
    cssClass = isSeventyPercentVariation ? "small-width" : "wide";
  } else if (imageSmall) {
    promotionalType = 3;
    cssClass = "alt";
  }

  const slugDescribedId = (slug: string) =>
    slug
      ?.toLowerCase()
      .trim()
      .replace(/[\s/_]+/g, "-")
      .replace(/[^a-z0-9-]/g, "") || "item";

  const describedId = `read-more-${slugDescribedId(title)}`;

  return (
    <Grid row>
      <Grid column sm={12}>
        <div
          className={clsx(
            "component",
            styles["promotional-articles"],
            styles.solo,
            reverse && styles.reverse,
            isLight ? styles.white : styles.dark,
            styles[cssClass],
            isVisible && styles.animate,
            titleStyle
          )}
          ref={ref}
        >
          <div className={styles.wrapper}>
            <div className={styles.container}>
              <article>
                {imageMobile && linkUrlForRender && (
                  <Link
                    href={linkUrlForRender.fields.url || "#"}
                    className={styles["link"]}
                    aria-hidden="true"
                    tabIndex={-1}
                  >
                    <NextContentfulImage
                      src={imageMobile.fields.file.url}
                      alt={imageMobile.fields.description || ""}
                      width={imageMobile.fields.file.details.image.width}
                      height={imageMobile.fields.file.details.image.height}
                    />
                  </Link>
                )}
                <div className={styles.text}>
                  {(promotionalType === 1 || promotionalType === 2) && (
                    <div className={styles.line}></div>
                  )}
                  {label && <p className={styles.label}>{label}</p>}
                  {titleToRender && (
                    <h4 id={describedId}>
                      {titleUrl ? (
                        <Link href={titleUrl}>
                          {titleToRender}
                        </Link>
                      ) : (
                        titleToRender
                      )}
                    </h4>
                  )}
                  {description && <p>{description}</p>}
                  {articleLink && (
                    <Cta
                      {...articleLink.fields}
                      type="secondary"
                      light={
                        (promotionalType === 1 || promotionalType === 2) &&
                        !isLight
                      }
                      openInNewTab={articleLink.fields.openInNewTab}
                      ariaDescribedBy={describedId}
                    />
                  )}
                </div>
              </article>
            </div>
          </div>
        </div>
      </Grid>
    </Grid>
  );
};

export default PromotionalArticles;
